/**
 * The "Achievements" tab: what the user has earned — system achievements, achievements inside
 * collections, and collections with their badges — with Revoke and Restore.
 *
 * Revoking is STICKY. Achievements are recomputed from raw data (nightly, and after every change),
 * so simply taking one away would last until the next recompute handed it straight back. A revoked
 * row instead stays revoked until an admin restores it, whatever the user's data says, and the list
 * shows who revoked it, when and why. Restoring lifts the block; the achievement then counts again
 * only if the data still supports it.
 */

import { useState } from 'react'
import { Alert, Box, Button, Chip, Typography } from '@mui/material'
import { CollectionsBookmark, EmojiEvents, MilitaryTech, RemoveCircleOutline, Restore } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { userDataApi } from '@api/endpoints/userData'
import type {
  AdminRevocationDto,
  AdminUserCollectionAchievementDto,
  AdminUserCollectionDto,
  AdminUserDataChangeResultDto,
  AdminUserSystemAchievementDto,
} from '@api/types/userData'
import { UserDataActionDialog } from './UserDataActionDialog'
import { UserDataChangeResultDialog } from './UserDataChangeResultDialog'
import { UserDataSection } from './UserDataSection'
import { adminUserAchievementsKey } from './userQueryKeys'
import { errorMessage, formatDate, formatDay } from './userFormatting'
import { useTablePaging } from './useTablePaging'
import { useUserDataChange } from './useUserDataChange'
import { useIsAdmin } from './useIsAdmin'

export interface UserAchievementsPanelProps {
  userId: string
}

type Kind = 'system' | 'collectionAchievement' | 'collection'
type Mode = 'revoke' | 'restore'

interface AchievementAction {
  kind: Kind
  mode: Mode
  id: string
  name: string
  /** The collection an achievement belongs to, or the badge a collection carries. */
  context?: string | null
}

type ChangeCall = (userId: string, id: string, reason: string) => Promise<AdminUserDataChangeResultDto>

const CALLS: Record<Kind, Record<Mode, ChangeCall>> = {
  system: { revoke: userDataApi.revokeSystemAchievement, restore: userDataApi.restoreSystemAchievement },
  collectionAchievement: {
    revoke: userDataApi.revokeCollectionAchievement,
    restore: userDataApi.restoreCollectionAchievement,
  },
  collection: { revoke: userDataApi.revokeCollection, restore: userDataApi.restoreCollection },
}

/** The dialog copy. Each spells out that a revoke outlasts recalculation, because that is the surprise. */
function describe(action: AchievementAction): { title: string; message: string } {
  const { kind, mode, name, context } = action

  if (mode === 'restore') {
    return {
      title: `Restore ${name}?`,
      message:
        kind === 'collection'
          ? 'Lifts the revocation. The collection completes and its badge returns only if the user still has every achievement it needs.'
          : 'Lifts the revocation. It counts again only if the user’s progress still meets it.',
    }
  }

  switch (kind) {
    case 'system':
      return {
        title: `Revoke ${name}?`,
        message:
          'Its points come off the user’s total. It stays revoked until an admin restores it — even if their data still qualifies and achievements are recalculated.',
      }
    case 'collectionAchievement':
      return {
        title: `Revoke ${name}?`,
        message: `It stops counting toward ${context || 'its collection'}, which can cost that collection its completion and badge. It stays revoked until an admin restores it, even if the user completes it again.`,
      }
    case 'collection':
      return {
        title: `Revoke ${name}?`,
        message: `Takes the collection’s completion and ${context ? `the badge “${context}”` : 'its badge'}. It stays revoked until an admin restores it, however many of its achievements the user holds.`,
      }
  }
}

/** Name, a secondary line, and — for a revoked row — who took it away, when and why. Spans only:
 *  in card mode this renders inside the card's title element. */
function NameCell({ name, secondary, revocation }: { name: string; secondary?: string; revocation?: AdminRevocationDto | null }) {
  return (
    <>
      <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 500 }}>
        {name}
      </Typography>
      {secondary && (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {secondary}
        </Typography>
      )}
      {revocation && (
        <Typography component="span" variant="caption" color="error" sx={{ display: 'block' }}>
          Revoked {formatDate(revocation.revokedAt)} by {revocation.revokedByName || revocation.revokedByUserId || 'unknown'}
          {revocation.reason ? ` — “${revocation.reason}”` : ''}
        </Typography>
      )}
    </>
  )
}

function StatusCell({ revoked, completed, date, earnedLabel = 'Earned' }: { revoked: boolean; completed: boolean; date?: string | null; earnedLabel?: string }) {
  if (revoked) return <Chip size="small" color="error" label="Revoked" />
  if (!completed) return <Chip size="small" variant="outlined" label="Not complete" />
  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
      <Chip size="small" color="success" variant="outlined" label={earnedLabel} />
      {date && (
        <Typography variant="caption" color="text.secondary">
          {formatDay(date)}
        </Typography>
      )}
    </Box>
  )
}

function RevokeRestoreButton({ revoked, onClick }: { revoked: boolean; onClick: () => void }) {
  return revoked ? (
    <Button size="small" startIcon={<Restore />} onClick={onClick}>
      Restore
    </Button>
  ) : (
    <Button size="small" color="error" startIcon={<RemoveCircleOutline />} onClick={onClick}>
      Revoke
    </Button>
  )
}

export function UserAchievementsPanel({ userId }: UserAchievementsPanelProps) {
  const change = useUserDataChange(userId)
  // Moderators see the lists read-only; revoke/restore is Admin-only on the API too.
  const isAdmin = useIsAdmin()
  const [action, setAction] = useState<AchievementAction | null>(null)

  const systemPaging = useTablePaging()
  const collectionAchievementPaging = useTablePaging()
  const collectionPaging = useTablePaging()

  const { data, isLoading, error } = useQuery({
    queryKey: adminUserAchievementsKey(userId),
    queryFn: () => userDataApi.getAchievements(userId),
  })

  const loadError = error ? errorMessage(error, 'Failed to load achievements') : null

  const open = (kind: Kind, id: string, name: string, revoked: boolean, context?: string | null) =>
    setAction({ kind, mode: revoked ? 'restore' : 'revoke', id, name, context })

  const close = () => {
    setAction(null)
    change.resetError()
  }

  const systemColumns: DataTableColumn<AdminUserSystemAchievementDto>[] = [
    {
      key: 'name',
      header: 'Achievement',
      primary: true,
      render: (a) => <NameCell name={a.name} secondary={a.criteriaType} revocation={a.revocation} />,
    },
    { key: 'points', header: 'Points', align: 'right', render: (a) => a.points.toLocaleString() },
    {
      key: 'progress',
      header: 'Progress',
      hideBelow: 'md',
      render: (a) => `${a.currentValue.toLocaleString()} / ${a.threshold.toLocaleString()}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <StatusCell revoked={Boolean(a.revocation)} completed={a.isCompleted} date={a.completedDate} />,
    },
  ]

  const collectionAchievementColumns: DataTableColumn<AdminUserCollectionAchievementDto>[] = [
    {
      key: 'name',
      header: 'Achievement',
      primary: true,
      render: (a) => <NameCell name={a.name} secondary={a.collectionName} revocation={a.revocation} />,
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (a) => `${a.completedCount.toLocaleString()} / ${a.requiredCount.toLocaleString()}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <StatusCell revoked={Boolean(a.revocation)} completed={a.isCompleted} date={a.completedDate} />,
    },
  ]

  const collectionColumns: DataTableColumn<AdminUserCollectionDto>[] = [
    {
      key: 'name',
      header: 'Collection',
      primary: true,
      render: (c) => <NameCell name={c.name} revocation={c.revocation} />,
    },
    {
      key: 'progress',
      header: 'Achievements',
      render: (c) => `${c.completedAchievementCount.toLocaleString()} / ${c.totalAchievementCount.toLocaleString()}`,
    },
    {
      key: 'badge',
      header: 'Badge',
      render: (c) =>
        c.hasBadge ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <MilitaryTech fontSize="small" color="warning" />
            <span>
              {c.badgeName || 'Badge'}
              {c.badgeEarnedDate ? ` · ${formatDay(c.badgeEarnedDate)}` : ''}
            </span>
          </Box>
        ) : c.badgeName ? (
          <Typography variant="body2" color="text.secondary" component="span">
            Not held ({c.badgeName})
          </Typography>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => (
        <StatusCell revoked={Boolean(c.revocation)} completed={c.isCompleted} date={c.completedDate} earnedLabel="Complete" />
      ),
    },
  ]

  const described = action ? describe(action) : null

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        A revoke is permanent until restored: recalculation never re-grants a revoked achievement, collection or badge,
        even when the user&rsquo;s data still qualifies. Award buttons are on the Overview tab.
      </Alert>

      <UserDataSection
        title="System achievements"
        icon={<EmojiEvents fontSize="small" />}
        description="The achievements that carry points and rank."
      >
        <DataTable
          rows={data?.system ?? []}
          columns={systemColumns}
          getRowKey={(a) => a.achievementId}
          loading={isLoading}
          error={loadError}
          emptyMessage="No system achievements earned"
          dense
          {...systemPaging}
          rowActions={isAdmin ? (a) => (
            <RevokeRestoreButton
              revoked={Boolean(a.revocation)}
              onClick={() => open('system', a.achievementId, a.name, Boolean(a.revocation))}
            />
          ) : undefined}
        />
      </UserDataSection>

      <UserDataSection
        title="Collection achievements"
        icon={<EmojiEvents fontSize="small" />}
        description="Achievements inside collections. Revoking one can cost its collection the completion and badge."
      >
        <DataTable
          rows={data?.collectionAchievements ?? []}
          columns={collectionAchievementColumns}
          getRowKey={(a) => a.achievementId}
          loading={isLoading}
          error={loadError}
          emptyMessage="No collection achievements completed"
          dense
          {...collectionAchievementPaging}
          rowActions={isAdmin ? (a) => (
            <RevokeRestoreButton
              revoked={Boolean(a.revocation)}
              onClick={() =>
                open('collectionAchievement', a.achievementId, a.name, Boolean(a.revocation), a.collectionName)
              }
            />
          ) : undefined}
        />
      </UserDataSection>

      <UserDataSection
        title="Collections and badges"
        icon={<CollectionsBookmark fontSize="small" />}
        description="Revoking a collection takes its completion and its badge."
      >
        <DataTable
          rows={data?.collections ?? []}
          columns={collectionColumns}
          getRowKey={(c) => c.collectionId}
          loading={isLoading}
          error={loadError}
          emptyMessage="No collections completed"
          dense
          {...collectionPaging}
          rowActions={isAdmin ? (c) => (
            <RevokeRestoreButton
              revoked={Boolean(c.revocation)}
              onClick={() => open('collection', c.collectionId, c.name, Boolean(c.revocation), c.badgeName)}
            />
          ) : undefined}
        />
      </UserDataSection>

      {action && described && (
        <UserDataActionDialog
          title={described.title}
          message={described.message}
          confirmLabel={action.mode === 'revoke' ? 'Revoke' : 'Restore'}
          destructive={action.mode === 'revoke'}
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) =>
            change.submit(() => CALLS[action.kind][action.mode](userId, action.id, reason), close)
          }
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </Box>
  )
}
